/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { updateUserAttribute, updatePassword, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api-config';

const FACEBOOK_APP_ID = "1245931220393560";
const X_CLIENT_ID = "SzlrUUlfdnNPMEVRbG9jdHpDQ3E6MTpjaQ";
const LINKEDIN_CLIENT_ID = "78jt4f1t3iq744";

const AccountSettingsView = ({ email, setMessage }) => {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [emailLoading, setEmailLoading] = useState(true);
  const [isOAuthProcessing, setIsOAuthProcessing] = useState(false);
  
  const navigate = useNavigate();

  // FIX: Proper email initialization on component mount
  useEffect(() => {
    initializeUserEmail();
    
    // Wait for URL parameters to be updated
    setTimeout(() => {
      const params = new URLSearchParams(window.location.search);

      const hasOAuthParams = params.has('code') || params.has('error') || params.has('state');
      if (hasOAuthParams) {
        setIsOAuthProcessing(true);
        
        // Set timeout to hide overlay after 10 seconds as fallback
        setTimeout(() => setIsOAuthProcessing(false), 10000);
      }
      
      // Handle various success/error states
      handleUrlParameters(params);
      
      // Clear URL parameters after handling
      if (params.toString()) {
        window.history.replaceState({}, document.title, "/account-settings");
      }
      
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIX: New function to properly initialize email
  const initializeUserEmail = async () => {
    try {
      setEmailLoading(true);
      
      // If email prop is valid, use it
      if (email && email.includes('@') && !email.includes('us-east-1_')) {
        setNewEmail(email);
        setEmailLoading(false);
        return;
      }
      
      // Otherwise, fetch from Cognito directly
      const user = await getCurrentUser();
      const userEmail = user.signInDetails?.loginId || user.username;
      
      if (userEmail && userEmail.includes('@')) {
        setNewEmail(userEmail);
      }
    } catch (error) {
      console.error("Error fetching user email:", error);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleOAuthReturnRedirect = () => {
    const returnPath = sessionStorage.getItem("oauth_return_path");
    const returnContext = sessionStorage.getItem("oauth_return_context");

    if (returnPath && returnContext === "feed_settings") {
      sessionStorage.removeItem("oauth_return_path");
      sessionStorage.removeItem("oauth_return_context");

      // Validate returnPath is a safe relative path
      if (returnPath.startsWith('/') && !returnPath.startsWith('//') && !returnPath.includes('://')) {
        window.location.href = returnPath;
        return true;
      }
      console.warn("Rejected unsafe redirect path:", returnPath);
      return false;
    }
    return false;
  };


  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const generateCodeChallenge = async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const handleXAuthCallback = async (code) => {
  if (!code) {
    console.error("❌ Missing X authorization code.");
    return;
  }

  const codeVerifier = sessionStorage.getItem("x_code_verifier");

  if (!codeVerifier) {
    console.error("❌ Missing `codeVerifier`. Authentication will fail.");
    return;
  }

  try {
    console.log("✅ Found X OAuth code:", code);

    const session = await fetchAuthSession();
    const userId = session.tokens?.idToken?.payload?.sub;

    // Check for feed-level authentication context
    const feedId = sessionStorage.getItem("oauth_feedId");
    console.log("🔍 X auth context:", {
      userId,
      feedId: feedId || "none (account-level)",
      hasCode: !!code,
      hasCodeVerifier: !!codeVerifier
    });

    console.log("📡 Sending X OAuth code to backend...");
    
    // Prepare request body - include feedId if it exists
    const requestBody = { code, codeVerifier, userId };
    if (feedId) {
      requestBody.feedId = feedId;
      console.log("🔍 Including feedId in request:", feedId);
    } else {
      console.log("🔍 No feedId found - account-level connection");
    }
    
    const response = await fetch(`${API_BASE_URL}/x/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.success) {
      console.log("✅ X authentication successful!");
      
      const successMessage = feedId 
        ? `✅ X (Twitter) connected successfully for feed!`
        : "✅ X (Twitter) connected successfully!";
      
      
      // ADD THESE LINES:
      const didRedirect = handleOAuthReturnRedirect();
      if (!didRedirect) {
        window.history.replaceState({}, document.title, "/account-settings");
      }
    } else {
      console.error("❌ X authentication failed:", data.error);
      const errorMessage = `X connection failed: ${data.error}`;
      handleOAuthReturnRedirect(); // ADD THIS LINE
    }
  } catch (error) {
    console.error("❌ Error during X auth request:", error);
  }

  // Clean up sessionStorage
  sessionStorage.removeItem("x_code_verifier");
  sessionStorage.removeItem("oauth_feedId");
};


  const handleLinkedInAuthCallback = async (code) => {
  if (!code) {
    console.error("❌ Missing LinkedIn authorization code.");
    return;
  }

  try {
    console.log("✅ Found LinkedIn OAuth code:", code);

    const session = await fetchAuthSession();
    const userId = session.tokens?.idToken?.payload?.sub;

    // Check for feed-level authentication context
    const feedId = sessionStorage.getItem("oauth_feedId");
    console.log("🔍 LinkedIn auth context:", {
      userId,
      feedId: feedId || "none (account-level)",
      hasCode: !!code
    });

    console.log("📡 Sending LinkedIn OAuth code to backend...");
    
    // Prepare request body - include feedId if it exists
    const requestBody = { code, userId };
    if (feedId) {
      requestBody.feedId = feedId;
    }
    
    const response = await fetch(`${API_BASE_URL}/linkedin/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.success) {
      console.log("✅ LinkedIn authentication successful!");
      
      const successMessage = feedId 
        ? `✅ LinkedIn connected successfully for feed!`
        : "✅ LinkedIn connected successfully!";
      
      
      // ADD THESE LINES:
      const didRedirect = handleOAuthReturnRedirect();
      if (!didRedirect) {
        window.history.replaceState({}, document.title, "/account-settings");
      }
    } else {
      console.error("❌ LinkedIn authentication failed:", data.error);
      const errorMessage = `LinkedIn connection failed: ${data.error}`;
      handleOAuthReturnRedirect(); // ADD THIS LINE
    }
  } catch (error) {
    console.error("❌ Error during LinkedIn auth request:", error);
  }

  // Clean up sessionStorage
  sessionStorage.removeItem("oauth_feedId");
  sessionStorage.removeItem("linkedin_code_verifier");
};


  const handleUrlParameters = (params) => {
    // Handle Facebook errors
    if (params.get("facebook_error")) {
      const errorType = params.get("facebook_error");
      const errorDescription = params.get("error_description") || '';
      const debugInfoParam = params.get("debug_info");
      const missingPermissions = params.get("missing_permissions");
      const userName = params.get("user_name");
      
      let errorMessage = "❌ Facebook connection failed: ";
      
      switch (errorType) {
        case "no_pages":
          errorMessage += "No Facebook pages found. ";
          if (userName) {
            errorMessage += `User: ${userName}. `;
          }
          errorMessage += "Make sure you admin at least one Facebook page, or that you granted all requested permissions.";
          if (missingPermissions) {
            errorMessage += ` Missing permissions: ${missingPermissions}`;
          }
          break;
        case "insufficient_permissions":
          errorMessage += "Insufficient permissions granted. ";
          if (missingPermissions) {
            errorMessage += `Please grant these permissions: ${missingPermissions.replace(/,/g, ', ')}`;
          }
          break;
        case "auth_failed":
          errorMessage += errorDescription || "Authentication failed";
          break;
        default:
          errorMessage += errorDescription || "Unknown error occurred";
      }
      
      setMessage(errorMessage);
      
      // Set debug info if available
      if (debugInfoParam) {
        try {
          const debugData = JSON.parse(decodeURIComponent(debugInfoParam));
          setDebugInfo(JSON.stringify(debugData, null, 2));
        } catch (e) {
          setDebugInfo(debugInfoParam);
        }
      }
    }

    // Handle other OAuth callbacks...
    const code = params.get("code");
    const state = params.get("state");
    const provider = sessionStorage.getItem("oauth_provider");

    if (code && provider === "x") {
      console.log("🔍 X Found OAuth code, calling handleXAuthCallback...");
      console.log("🔍 State parameter:", state);
      handleXAuthCallback(params.get("code"));
    }

    if (code && provider === "linkedin") {
      console.log("🔍 Found LinkedIn OAuth code, calling handleLinkedInAuthCallback...");
      handleLinkedInAuthCallback(params.get("code"));
    }
    
    sessionStorage.removeItem("oauth_provider");
    setTimeout(() => setIsOAuthProcessing(false), 500);
  };

  const handleEmailChange = async () => {
    try {
      setIsUpdating(true);
      
      // FIX: Use the current email value (which should now be properly initialized)
      const currentEmail = newEmail || email;
      await updateUserAttribute({ 
        username: currentEmail, 
        attribute: 'email', 
        value: newEmail 
      });
      setMessage('Email updated successfully!');
    } catch (error) {
      setMessage(`Error updating email: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setIsUpdating(true);
      await updatePassword({ 
        oldPassword: currentPassword, 
        newPassword: newPassword 
      });
      setMessage('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      setMessage(`Error updating password: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="account-settings-container" id="accountSettingsContainer">
     
      {/* OAuth Processing Overlay */}
      {isOAuthProcessing && (
        <div className="oauth-loading-overlay">
          <div className="oauth-loading-content">
            <div className="oauth-spinner"></div>
            <h3 className="oauth-loading-title">Completing authentication...</h3>
            <p className="oauth-loading-text">You'll be redirected shortly</p>
          </div>
        </div>
      )}

      {/* Debug Info */}
      {debugInfo && (
        <div className="debug-info bg-gray-100 p-4 mb-6 rounded-lg" id="debugInfo">
          <h4 className="font-medium text-gray-800 mb-2">Debug Info:</h4>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap">{debugInfo}</pre>
        </div>
      )}

      {/* Account Information Card */}
      <div className="account-info-card bg-white rounded-lg p-6 mb-6 border border-gray-200 shadow-sm" id="accountInfoCard">
        <h3 className="account-info-title text-xl font-semibold mb-6" id="accountInfoTitle">Account Information</h3>
        
        <form className="account-info-form space-y-6" id="accountInfoForm">
          {/* Email Section */}
          <div className="account-email-section" id="emailSection">
            <label className="account-email-label block text-sm font-medium text-gray-700 mb-2" id="emailLabel">
              Email Address
            </label>
            <div className="account-email-form flex gap-3" id="emailForm">
              {emailLoading ? (
                <div className="flex-1 p-3 bg-gray-100 border border-gray-300 rounded-md">
                  <span className="text-gray-500">Loading email...</span>
                </div>
              ) : (
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="account-email-input flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email address"
                  id="emailInput"
                />
              )}
              <button
                type="button"
                onClick={handleEmailChange}
                disabled={isUpdating || emailLoading || !newEmail}
                className="account-email-btn px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                id="emailUpdateBtn"
              >
                {isUpdating ? 'Updating...' : 'Update Email'}
              </button>
            </div>
          </div>

          {/* Password Section */}
          <div className="account-password-section border-t border-gray-200 pt-6" id="passwordSection">
            <h4 className="account-password-title text-lg font-semibold mb-4" id="passwordTitle">Change Password</h4>
            <div className="account-password-form grid grid-cols-1 md:grid-cols-2 gap-4" id="passwordForm">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" id="currentPasswordLabel">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  id="currentPasswordInput"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" id="newPasswordLabel">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  id="newPasswordInput"
                />
              </div>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={handlePasswordChange}
                disabled={isUpdating || !currentPassword || !newPassword}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                id="passwordUpdateBtn"
              >
                {isUpdating ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
export default AccountSettingsView;